import React from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
import classnames from 'classnames';
import emptyFunction from 'common/util/emptyFunction';

import {
  JOB_TYPE_SUNLOCK_INSTALL,
  JOB_TYPE_YARD_SIGN_SETUP,
  JOB_TYPE_CODEBOX_INSTALL,
  JOB_TYPE_MEDIA,
  JOB_TYPE_CONDITION_REPORT,
  JOB_TYPE_REPLACE_SUNLOCK_BATTERY,
  JOB_TYPE_CHECK_DOOR_LOCK,
  JOB_STATUS_COMPLETED,
  JOB_STATUS_FAILED,
  srJobStatusToDriverStatusMapping,
  getJobTypeToActionMapping,
  JOB_TYPE_MEDIA_CURBSHOT,
} from 'common/jobConstants';

import {
  PARTNER_ID_HOMERIVER,
} from 'common/partnerConstants';

import {
  scrollToFormField,
  createInitialValues,
  createFormValidationFn,
  createFormFieldsByIdGetter,
} from 'client/helpers/formikHelpers';

import SrFormikField, {
  SR_FIELD_THEME_ADOBE,
} from 'client/components/SrFormikField';

import ButtonDeprecated, {
  BUTTON_THEME_RED_ADOBE,
  BUTTON_THEME_GREEN_ADOBE,
} from 'client/components/ButtonDeprecated';
import {
  SR_CHECKBOX_THEME_INLINE_WRAP,
} from 'client/components/Checkbox';
import SrBox from 'client/components/SrBox';
import SrSunSetupDriverJobSunlockTest from 'client/components/SrSunSetupDriverJob/SrSunSetupDriverJobSunlockTest';
import SrSunSetupDriverJobSunlockBarcode from 'client/components/SrSunSetupDriverJob/SrSunSetupDriverJobSunlockBarcode';
import SrSunSetupDriverJobCodeboxBarcode from 'client/components/SrSunSetupDriverJob/SrSunSetupDriverJobCodeboxBarcode';
import SrSunSetupDriverJobYardSignSetup from 'client/components/SrSunSetupDriverJob/SrSunSetupDriverJobYardSignSetup';
import SrSunSetupDriverJobCompletionPhotos from 'client/components/SrSunSetupDriverJob/SrSunSetupDriverJobCompletionPhotos';
import SrSunSetupDriverJobMediaPhotos from 'client/components/SrSunSetupDriverJob/SrSunSetupDriverJobMediaPhotos';
import SrSunSetupDriverJobConditionReportLink from 'client/components/SrSunSetupDriverJob/SrSunSetupDriverJobConditionReportLink';
import SrSunSetupDriverJobSunlockCodes from 'client/components/SrSunSetupDriverJob/SrSunSetupDriverJobSunlockCodes';
import SrSunSetupDriverJobCodeboxCodes from 'client/components/SrSunSetupDriverJob/SrSunSetupDriverJobCodeboxCodes';
import SrSunSetupDriverJobCodeboxCheck from 'client/components/SrSunSetupDriverJob/SrSunSetupDriverJobCodeboxCheck';
import SrSunSetupDriverJobCodeboxLocationPhotos from 'client/components/SrSunSetupDriverJob/SrSunSetupDriverJobCodeboxLocationPhotos';
import SrSunSetupDriverJobCodeboxSpecialInstructions from 'client/components/SrSunSetupDriverJob/SrSunSetupDriverJobCodeboxSpecialInstructions';

import getJobChecklist from 'client/components/SrSunSetupDriverJob/getJobChecklist';
import './SrSunSetupDriverJobPage.less';

function getFormFields({
  job,
  listing,
  sunlock,
  codeboxLock,
  formValues = {},
  onCompletionPhotoUrls,
  onCodeboxLocationPhotoUrls,
  onCodeboxInstructions,
  onDriverNotesChange,
  onChecklistChange,
}) {
  const formFields = [
    {
      id: 'status',
      type: 'hidden',
    },
  ];

  const checklist = getJobChecklist({ job, listing });
  if (checklist) {
    formFields.push({
      id: 'checklist',
      title: 'Instructions',
      type: 'multiple_choice',
      validations: formValues.status === JOB_STATUS_COMPLETED
        ? ['checklistCompleted']
        : [],
      properties: {
        autoFitWidth: '100%',
        multipleSelection: true,
        useArrayValues: true,
        choices: checklist.map((point) => ({
          label: point.label,
          value: point.value,
        })),
        onChange: onChecklistChange,
        fieldType: SR_CHECKBOX_THEME_INLINE_WRAP,
      },
    });
  }

  formFields.push({
    id: 'completionPhotoUrls',
    title: '',
    type: 'file_multi',
    validations: formValues.status === JOB_STATUS_COMPLETED
      ? ['nonEmptyArray']
      : [],
    properties: {
      filePath: '/job-uploads/completion-photos/{uuid}',
      fileAccept: 'image/*, .heic',
      onChange: onCompletionPhotoUrls,
    },
  });

  formFields.push({
    id: 'driverNotes',
    title: 'Driver Notes',
    type: 'long_text',
    validations: formValues.status === JOB_STATUS_FAILED
      ? ['required']
      : [],
    properties: {
      placeholder: 'Write notes here...',
      onBlur: onDriverNotesChange,
    },
  });

  if (
    job.jobTypeId === JOB_TYPE_SUNLOCK_INSTALL
    || job.jobTypeId === JOB_TYPE_REPLACE_SUNLOCK_BATTERY
    || job.jobTypeId === JOB_TYPE_CHECK_DOOR_LOCK
  ) {
    formFields.push({
      id: 'sunlockId',
      type: 'hidden',
      validations: formValues.status === JOB_STATUS_COMPLETED
        ? ['required::Please register a sunlock.']
        : [],
    });
  }

  if (job.jobTypeId === JOB_TYPE_CODEBOX_INSTALL) {
    formFields.push({
      id: 'codeboxLockId',
      type: 'hidden',
      validations: formValues.status === JOB_STATUS_COMPLETED
        ? ['required::Please register a codebox.']
        : [],
    });

    formFields.push({
      id: 'codeboxLocationPhotos',
      title: '',
      type: 'file_multi',
      validations: formValues.status === JOB_STATUS_COMPLETED
        ? ['nonEmptyArray']
        : [],
      properties: {
        filePath: '/job-uploads/codebox-location-photos/{uuid}',
        fileAccept: 'image/*, .heic',
        onChange: onCodeboxLocationPhotoUrls,
      },
    });

    formFields.push({
      id: 'codeboxInstructions',
      title: '',
      type: 'long_text',
      properties: {
        placeholder: 'Write here...',
        onBlur: onCodeboxInstructions,
      },
    });
  }

  if (job.jobTypeId === JOB_TYPE_SUNLOCK_INSTALL || job.jobTypeId === JOB_TYPE_CODEBOX_INSTALL) {
    formFields.push({
      id: 'successfulTest',
      type: 'hidden',
      validations: formValues.status === JOB_STATUS_COMPLETED && (sunlock || codeboxLock)
        ? ['lockSuccessfulTest']
        : [],
    });
  }

  if (job.jobTypeId === JOB_TYPE_YARD_SIGN_SETUP && listing.partnerId !== PARTNER_ID_HOMERIVER) {
    formFields.push({
      id: 'yardSignUrl',
      type: 'hidden',
      validations: formValues.status === JOB_STATUS_COMPLETED
        ? ['required::Please register the yard sign.']
        : [],
    });
  }

  if (
    job.jobTypeId === JOB_TYPE_MEDIA
    || job.jobTypeId === JOB_TYPE_MEDIA_CURBSHOT
  ) {
    formFields.push({
      id: 'listingPhotoUrls',
      type: 'hidden',
      validations: formValues.status === JOB_STATUS_COMPLETED
        ? ['nonEmptyArray']
        : [],
    });
  }

  return formFields;
}

const SrSunSetupDriverJobPage = ({
  history,
  userAgent,
  job,
  sunlock,
  codeboxLock,
  yardSignUrl,
  listing,
  driverId,
  jobOrderId,
  onUpdateJob,
  onUpdateListing,
  onSunlockBarcodeText,
  onYardSignBarcodeText,
  onCodeboxBarcodeText,
  appUrl,
}) => {
  return (
    <Formik
      enableReinitialize
      onSubmit={emptyFunction}
      initialValues={createInitialValues({
        formFields: getFormFields({
          job,
          listing,
        }),
        defaultValues: {
          ...job,
          sunlockId: sunlock?.id,
          codeboxLockId: codeboxLock?.id,
          yardSignUrl: yardSignUrl,
          listingPhotoUrls: listing.photoUrls,
          codeboxLocationPhotos: listing.codeboxLocationPhotos,
          codeboxInstructions: listing.codeboxInstructions,
        },
      })}
      validate={(formValues) => {
        const formFields = getFormFields({
          job,
          sunlock,
          codeboxLock,
          listing,
          formValues,
        });
        const errors = createFormValidationFn(formFields)(formValues);
        return errors;
      }}
      render={(formikBag) => {
        const formFields = getFormFields({
          job,
          listing,
          sunlock,
          codeboxLock,
          formValues: formikBag.values,
          onChecklistChange: (checklist) => {
            formikBag.setFieldValue('checklist', checklist);
            onUpdateJob({
              updates: {
                checklist,
              },
            });
          },
          onCompletionPhotoUrls: (completionPhotoUrls) => {
            onUpdateJob({
              updates: {
                completionPhotoUrls,
              },
            });
          },
          onCodeboxLocationPhotoUrls: (codeboxLocationPhotos) => {
            onUpdateListing({
              updates: {
                codeboxLocationPhotos,
              },
            });
          },
          onCodeboxInstructions: () => {
            onUpdateListing({
              updates: {
                codeboxInstructions: formikBag.values.codeboxInstructions || null,
              },
            });
          },
          onDriverNotesChange: () => {
            onUpdateJob({
              updates: {
                driverNotes: formikBag.values.driverNotes || null,
              },
            });
          },
        });

        const onStatusChange = (status) => {
          formikBag.setFieldValue('status', status);

          const formValues = {
            ...formikBag.values,
            status,
          };

          const formErrors = createFormValidationFn(getFormFields({
            job,
            sunlock,
            codeboxLock,
            listing,
            formValues,
          }))(formValues);
          const formFielIdsWithErrors = Object.keys(formErrors);

          if (!formFielIdsWithErrors.length) {
            onUpdateJob({
              updates: {
                status,
              },
            }).then(() => {
              history.push(`/sunsetup/drivers/${driverId}/orders/${jobOrderId}`);
            });
          }
          else {
            formFielIdsWithErrors.forEach((formFieldId) => {
              formikBag.setFieldTouched(formFieldId, true, true);
            });
            scrollToFormField({
              formFieldId: formFielIdsWithErrors[0],
            });
          }
        };

        const getFormFieldsById = createFormFieldsByIdGetter(formFields);
        const checklistFormField = getFormFieldsById('checklist');

        return (
          <Form>
            <div styleName='card'>
              <span styleName='jobType'>
                {getJobTypeToActionMapping({ job, listing })}
              </span>
              <br />
              <span styleName={classnames('jobStatus', job.status.toLowerCase())}>
                {srJobStatusToDriverStatusMapping[job.status]}
              </span>
            </div>

            {checklistFormField && (
              <div styleName='card'>
                <SrFormikField
                  formField={getFormFieldsById('checklist')}
                  formikBag={formikBag}
                  theme={SR_FIELD_THEME_ADOBE}
                />
              </div>
            )}

            {[JOB_TYPE_MEDIA, JOB_TYPE_MEDIA_CURBSHOT].includes(job.jobTypeId) && (
              <SrSunSetupDriverJobMediaPhotos
                listingId={listing.id}
                driverId={driverId}
                jobOrderId={jobOrderId}
                jobId={job.id}
                formikBag={formikBag}
              />
            )}

            {job.jobTypeId === JOB_TYPE_CONDITION_REPORT && (
              <SrSunSetupDriverJobConditionReportLink
                userAgent={userAgent}
              />
            )}

            {job.jobTypeId === JOB_TYPE_SUNLOCK_INSTALL && (
              <SrSunSetupDriverJobSunlockBarcode
                userAgent={userAgent}
                sunlock={sunlock}
                formikBag={formikBag}
                onSunlockBarcodeText={onSunlockBarcodeText}
              />
            )}

            {job.jobTypeId === JOB_TYPE_CODEBOX_INSTALL && (
              <SrSunSetupDriverJobCodeboxBarcode
                userAgent={userAgent}
                codeboxLock={codeboxLock}
                formikBag={formikBag}
                onCodeboxBarcodeText={onCodeboxBarcodeText}
              />
            )}

            {job.jobTypeId === JOB_TYPE_CODEBOX_INSTALL && (
              <SrSunSetupDriverJobCodeboxCodes
                codeboxLock={codeboxLock}
              />
            )}

            {job.jobTypeId === JOB_TYPE_CODEBOX_INSTALL && codeboxLock && (
              <SrSunSetupDriverJobCodeboxCheck
                userAgent={userAgent}
                formikBag={formikBag}
                appUrl={appUrl}
                listing={listing}
              />
            )}

            {job.jobTypeId === JOB_TYPE_CODEBOX_INSTALL && (
              <SrSunSetupDriverJobCodeboxLocationPhotos
                formikBag={formikBag}
                getFormFieldsById={getFormFieldsById}
              />
            )}

            {job.jobTypeId === JOB_TYPE_CODEBOX_INSTALL && (
              <SrSunSetupDriverJobCodeboxSpecialInstructions
                formikBag={formikBag}
                getFormFieldsById={getFormFieldsById}
              />
            )}

            {job.jobTypeId === JOB_TYPE_SUNLOCK_INSTALL && (
              <SrSunSetupDriverJobSunlockCodes
                sunlock={sunlock}
              />
            )}

            {job.jobTypeId === JOB_TYPE_YARD_SIGN_SETUP && listing.partnerId !== PARTNER_ID_HOMERIVER && (
              <SrSunSetupDriverJobYardSignSetup
                formikBag={formikBag}
                onYardSignBarcodeText={onYardSignBarcodeText}
                yardSignUrl={yardSignUrl}
              />
            )}

            {sunlock && (
              job.jobTypeId === JOB_TYPE_SUNLOCK_INSTALL
              || job.jobTypeId === JOB_TYPE_REPLACE_SUNLOCK_BATTERY
              || job.jobTypeId === JOB_TYPE_CHECK_DOOR_LOCK
            )
            && (
              <SrSunSetupDriverJobSunlockTest
                userAgent={userAgent}
                formikBag={formikBag}
                appUrl={appUrl}
                listing={listing}
              />
            )}

            <SrSunSetupDriverJobCompletionPhotos
              text={job.jobTypeId !== JOB_TYPE_CODEBOX_INSTALL ? null : (
                'The completion photo should show the CodeBox location and ' +
                'verify that, if the key string is installed, it is long ' +
                'enough to reach the door lock.'
              )}
              formikBag={formikBag}
              getFormFieldsById={getFormFieldsById}
            />

            <div styleName='card'>
              <SrFormikField
                formField={getFormFieldsById('driverNotes')}
                formikBag={formikBag}
                theme={SR_FIELD_THEME_ADOBE}
              />
            </div>

            {![JOB_STATUS_FAILED, JOB_STATUS_COMPLETED].includes(job.status) && (
              <SrBox margin='40px 0 0'>
                <ButtonDeprecated
                  theme={BUTTON_THEME_RED_ADOBE}
                  onClick={() => onStatusChange(JOB_STATUS_FAILED)}
                >
                  Incomplete
                </ButtonDeprecated>
                <SrBox height='18px' />
                <ButtonDeprecated
                  theme={BUTTON_THEME_GREEN_ADOBE}
                  onClick={() => onStatusChange(JOB_STATUS_COMPLETED)}
                >
                  Job Complete
                </ButtonDeprecated>
              </SrBox>
            )}
          </Form>
        );
      }}
    />
  );
};

SrSunSetupDriverJobPage.propTypes = {
  userAgent: PropTypes.object.isRequired,
  job: PropTypes.object.isRequired,
  sunlock: PropTypes.object,
  yardSignUrl: PropTypes.string,
  listing: PropTypes.object.isRequired,
  jobOrderId: PropTypes.number.isRequired,
  driverId: PropTypes.number.isRequired,
  onUpdateJob: PropTypes.func.isRequired,
  onUpdateListing: PropTypes.func.isRequired,
  onSunlockBarcodeText: PropTypes.func.isRequired,
  onYardSignBarcodeText: PropTypes.func.isRequired,
  onCodeboxBarcodeText: PropTypes.func.isRequired,
  appUrl: PropTypes.string.isRequired,
};

SrSunSetupDriverJobPage.displayName = 'SrSunSetupDriverJobPage';

export default SrSunSetupDriverJobPage;
